import { WarehousePolicySource, WarehouseView } from '@/console/api/types'
import { Callout } from '@/console/ui/Callout'
import { Note } from '@/console/ui/Note'
import { OperationFlagsCard } from './OperationFlagsCard'
import { OutboundPrefixCard } from './OutboundPrefixCard'
import { ParcelCarrierCard } from './ParcelCarrierCard'
import { findPolicy } from './policyForm'
import { WriteCallbacks } from './usePolicySave'
import styles from './Warehouse.module.css'

/** 그 카드가 다루는 type 들의 서버 값 직렬화. 저장 성공 후 재조회로 값이 바뀌면 카드가 리마운트돼 폼이 서버 값으로 리셋된다. */
const keyOf = (view: WarehouseView, types: readonly Parameters<typeof findPolicy>[1][]): string =>
  `${view.warehouseCode}:${types.join('+')}:${JSON.stringify(types.map((type) => findPolicy(view, type) ?? null))}`

/**
 * LOGISPRD-15602 — 오른쪽 정책 편집 영역. 정책 원천(`source`)에 따라 카드를 그리거나 잠근다.
 *  - `none` — 정책을 읽을 수 없다. 모르는 값을 빈 폼으로 덮어쓰지 않게 카드 대신 잠금 안내만 보인다.
 *  - `activeList` — `get-warehouse` 가 null 이라 활성 목록 응답에서 읽었다. 편집은 같다.
 */
export const PolicyEditor = ({ source, ...callbacks }: WriteCallbacks & { source: WarehousePolicySource }): JSX.Element => {
  if (source.source === 'none') {
    return (
      <Callout tone="warn">
        이 창고의 정책을 읽을 수 없어 편집을 잠갔어요. (<code className={styles.inlineCode}>get-warehouse</code> 가 enum 에 없는 창고코드를 찾지
        못해요 — BE 후속)
      </Callout>
    )
  }

  const { view } = source
  const code = view.warehouseCode

  return (
    <div className={styles.cards}>
      {source.source === 'activeList' ? <p className={styles.sourceNote}>정책은 활성 창고 목록 응답에서 읽었어요.</p> : null}
      {/* 운영 플래그는 서버 값으로 리마운트하지 않는다(부분 실패 편집값 유지) — 창고가 바뀔 때만 새로 그린다. 이유는 OperationFlagsCard 주석. */}
      <OperationFlagsCard key={code} view={view} warehouseCode={code} {...callbacks} />
      <ParcelCarrierCard key={keyOf(view, ['PARCEL_CARRIER'])} view={view} warehouseCode={code} {...callbacks} />
      <OutboundPrefixCard key={keyOf(view, ['OUTBOUND_SEQUENCE_PREFIX'])} view={view} warehouseCode={code} {...callbacks} />
      <Note>
        정책은 type 1건씩 저장해요. 같은 type 은 통째로 교체되고 다른 type 은 그대로 남아요. 저장은 <b>MASTER</b> 또는{' '}
        <b>MOMS_ENG</b> 권한이 필요해요.
      </Note>
    </div>
  )
}
